/**
 * Header Component - Phase 2C-1 + Beta 3 War Room
 * 
 * Top navigation bar with branding, main navigation, and user menu.
 * 
 * CHANGES FOR WAR ROOM:
 * - Line 18: Changed nav label from 'Dashboard' to 'War Room'
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export function Header(): JSX.Element {
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  const navItems = [
      { path: '/', label: 'War Room' },  // <-- CHANGED: Was 'Dashboard'
      // { path: '/pools', label: 'Pools' },  // TD-001: Hidden - convert to leaderboard hub
    ];
  
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  return (
    <header className="h-16 bg-slate-800 border-b border-slate-700 sticky top-0 z-30">
      <div className="h-full px-6 flex items-center justify-between">
        {/* Logo & Brand */}
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <StarIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-100">
              North Star
            </span>
          </Link>
          
          {/* Main Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${isActive(item.path)
                    ? 'bg-slate-700 text-slate-100'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                  }
                `.trim()}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Admin Link */}
            {isAdmin && (
              <Link
                to="/admin"
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${location.pathname.startsWith('/admin')
                    ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30'
                    : 'text-purple-400 hover:bg-purple-600/10'
                  }
                `.trim()}
              >
                Admin
              </Link>
            )}
          </nav>
        </div>
        
        {/* Right Side - User Menu */}
        <div className="flex items-center gap-4">
          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg 
                         hover:bg-slate-700 transition-colors"
            >
              <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-slate-300">
                  {user?.display_name?.[0]?.toUpperCase() || user?.user_id?.[0]?.toUpperCase() || '?'}
                </span>
              </div>
              <span className="text-sm text-slate-300 hidden sm:block">
                {user?.display_name || user?.user_id || 'User'}
              </span>
              <ChevronDownIcon className="w-4 h-4 text-slate-400" />
            </button>
            
            {/* Dropdown Menu */}
            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-slate-700 
                                rounded-lg shadow-xl z-50 py-1">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-slate-700">
                    <p className="text-sm font-medium text-slate-200">
                      {user?.display_name || user?.user_id}
                    </p>
                    <p className="text-xs text-slate-400">{user?.email}</p>
                    {isAdmin && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium 
                                       bg-purple-600/20 text-purple-400 rounded">
                        Admin
                      </span>
                    )}
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-1">
                    <Link
                      to="/account"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-slate-300 
                                 hover:bg-slate-700 transition-colors"
                    >
                      Account Settings
                    </Link>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-slate-300 
                                 hover:bg-slate-700 transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * Star Icon for branding
 */
function StarIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

/**
 * Chevron Down Icon
 */
function ChevronDownIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export default Header;
